import { SimpleEvent, Editor, Vector, ComponentDom, Dom, Icons as Icons$1, InputView, OutputView, PlaceholderDirection, getAbsolutePosition, ClickCommandType, createLaunchPadStepComponentViewFactory, PlaceholderGapOrientation, RectPlaceholderView, RectPlaceholder, DefaultRegionView, JoinView, DefaultViewportController, SelectStepBehaviorEndToken, Badges, createTaskStepComponentViewFactory } from 'sequential-workflow-designer';

class ExternalControlBarController {
    static create() {
        return new ExternalControlBarController();
    }
    constructor() {
        this.api = null;
        this.onReady = new SimpleEvent();
        this.onStateChanged = new SimpleEvent();
        this.resetViewport = () => {
            this.getApi().viewport.resetViewport();
        };
        this.zoomIn = () => {
            this.getApi().viewport.zoom(true);
        };
        this.zoomOut = () => {
            this.getApi().viewport.zoom(false);
        };
        this.isUndoRedoSupported = () => {
            return this.getApi().controlBar.isUndoRedoSupported();
        };
        this.tryUndo = () => {
            return this.getApi().controlBar.tryUndo();
        };
        this.canUndo = () => {
            return this.getApi().controlBar.canUndo();
        };
        this.tryRedo = () => {
            return this.getApi().controlBar.tryRedo();
        };
        this.canRedo = () => {
            return this.getApi().controlBar.canRedo();
        };
        this.isDragDisabled = () => {
            return this.getApi().controlBar.isDragDisabled();
        };
        this.toggleIsDragDisabled = () => {
            this.getApi().controlBar.toggleIsDragDisabled();
        };
        this.setIsDragDisabled = (isDisabled) => {
            this.getApi().controlBar.setIsDragDisabled(isDisabled);
        };
        this.canDelete = () => {
            return this.getApi().controlBar.canDelete();
        };
        this.tryDelete = () => {
            return this.getApi().controlBar.tryDelete();
        };
        this.isReady = () => {
            return Boolean(this.api);
        };
        //
    }
    setApi(api) {
        if (api && this.api) {
            throw new Error('Api is already set');
        }
        if (this.api) {
            this.api.controlBar.onStateChanged.unsubscribe(this.onStateChanged.forward);
        }
        this.api = api;
        if (api) {
            api.controlBar.onStateChanged.subscribe(this.onStateChanged.forward);
            this.onReady.forward();
        }
    }
    getApi() {
        if (!this.api) {
            throw new Error('Designer is not ready yet');
        }
        return this.api;
    }
}

class ExternalControlBarDaemon {
    static create(api, configuration) {
        configuration.controller.setApi(api);
        return new ExternalControlBarDaemon(configuration.controller);
    }
    constructor(controller) {
        this.controller = controller;
    }
    destroy() {
        this.controller.setApi(null);
    }
}

class ExternalControlBarDaemonExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(api) {
        return ExternalControlBarDaemon.create(api, this.configuration);
    }
}

class CustomSelectedStepIdExternalEditorController {
    static create() {
        return new CustomSelectedStepIdExternalEditorController();
    }
    constructor() {
        this.onSelectedStepIdChanged = new SimpleEvent();
        this.selectedStepId = null;
        //
    }
    setSelectedStepId(selectedStepId) {
        const newId = selectedStepId || null; // nullify undefined
        if (this.selectedStepId !== newId) {
            this.selectedStepId = newId;
            this.onSelectedStepIdChanged.forward(newId);
        }
    }
}

class CustomSelectedStepIdStateModifierDependency {
    constructor(editorApi, controller, walker) {
        this.editorApi = editorApi;
        this.controller = controller;
        this.walker = walker;
    }
    update() {
        const selectedStepId = this.controller.selectedStepId;
        if (selectedStepId) {
            const definition = this.editorApi.getDefinition();
            const found = this.walker.findById(definition, selectedStepId);
            if (!found) {
                // If step is not found in the definition, clear the selected step id.
                this.controller.setSelectedStepId(null);
            }
        }
    }
}

class ExternalEditorDaemon {
    static create(api, configuration) {
        if (!configuration.placeholder) {
            throw new Error('Placeholder is not set');
        }
        const daemon = new ExternalEditorDaemon(api.editor, configuration);
        if (configuration.customSelectedStepIdExternalEditorController) {
            api.editor.addDefinitionModifierDependency(new CustomSelectedStepIdStateModifierDependency(api.editor, configuration.customSelectedStepIdExternalEditorController, api.definitionWalker));
        }
        daemon.setPlaceholder(configuration.placeholder);
        return daemon;
    }
    constructor(api, configuration) {
        this.api = api;
        this.configuration = configuration;
        this.editor = null;
    }
    setPlaceholder(placeholder) {
        this.editor = Editor.create(placeholder, this.api, 'sqd-external-editor sqd-external-step-editor', this.configuration.stepEditorProvider, 'sqd-external-editor sqd-external-root-editor', this.configuration.rootEditorProvider, this.configuration.customSelectedStepIdExternalEditorController || null);
    }
    destroy() {
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }
    }
}

class ExternalPathBarController {
    constructor() {
        this.api = null;
        this.onStateChanged = new SimpleEvent();
        this.setFolderPath = (path) => {
            this.getApi().setFolderPath(path);
        };
        this.getFolderPath = () => {
            return this.getApi().getFolderPath();
        };
        this.getFolderPathStepNames = () => {
            return this.getApi().getFolderPathStepNames();
        };
        this.isReady = () => {
            return Boolean(this.api);
        };
        this.onStateChangedHandler = () => this.onStateChanged.forward();
    }
    static create() {
        return new ExternalPathBarController();
    }
    setApi(api) {
        if (api && this.api) {
            throw new Error('Api is already set');
        }
        if (this.api) {
            this.api.onStateChanged.unsubscribe(this.onStateChangedHandler);
        }
        else if (api) {
            api.onStateChanged.subscribe(this.onStateChangedHandler);
        }
        this.api = api;
    }
    getApi() {
        if (!this.api) {
            throw new Error('Designer is not ready yet');
        }
        return this.api;
    }
}

class ExternalPathBarDaemon {
    static create(api, configuration) {
        configuration.controller.setApi(api.pathBar);
        return new ExternalPathBarDaemon(configuration.controller);
    }
    constructor(controller) {
        this.controller = controller;
    }
    destroy() {
        this.controller.setApi(null);
    }
}

class ExternalPathBarDaemonExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(api) {
        return ExternalPathBarDaemon.create(api, this.configuration);
    }
}

class ExternalToolboxController {
    static create() {
        return new ExternalToolboxController();
    }
    constructor() {
        this.api = null;
        this.tryDrag = (positionX, positionY, stepDefinition) => {
            return this.getApi().tryDrag(new Vector(positionX, positionY), stepDefinition);
        };
        this.isReady = () => {
            return !!this.api;
        };
        //
    }
    setApi(api) {
        if (api && this.api) {
            throw new Error('Api is already set');
        }
        this.api = api;
    }
    getApi() {
        if (!this.api) {
            throw new Error('Designer is not ready yet');
        }
        return this.api;
    }
}

class ExternalToolboxDaemon {
    static create(api, configuration) {
        configuration.controller.setApi(api.toolbox);
        return new ExternalToolboxDaemon(configuration.controller);
    }
    constructor(controller) {
        this.controller = controller;
    }
    destroy() {
        this.controller.setApi(null);
    }
}

class ExternalToolboxDaemonExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(api) {
        return ExternalToolboxDaemon.create(api, this.configuration);
    }
}

var IconBadgeFeature;
(function (IconBadgeFeature) {
    IconBadgeFeature[IconBadgeFeature["rotate"] = 1] = "rotate";
})(IconBadgeFeature || (IconBadgeFeature = {}));

var LoadingBadgeStatus;
(function (LoadingBadgeStatus) {
    LoadingBadgeStatus[LoadingBadgeStatus["loading"] = 1] = "loading";
    LoadingBadgeStatus[LoadingBadgeStatus["loaded"] = 2] = "loaded";
    LoadingBadgeStatus[LoadingBadgeStatus["error"] = 3] = "error";
})(LoadingBadgeStatus || (LoadingBadgeStatus = {}));

class CustomContextMenuExtension {
    static create(configuration) {
        return new CustomContextMenuExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    createItemsProvider(customActionController) {
        return {
            getItems: (step, sequence) => {
                const items = this.configuration.itemsProvider(step, sequence);
                return items.map((item, index) => {
                    const order = item.order || 100;
                    if (item.group) {
                        return {
                            label: item.group,
                            order
                        };
                    }
                    if (item.label) {
                        const label = item.label;
                        return {
                            label,
                            order,
                            callback: () => {
                                const action = {
                                    type: 'customMenuItemClicked',
                                    label,
                                    index
                                };
                                customActionController.trigger(action, step, sequence);
                            }
                        };
                    }
                    throw new Error('Invalid custom menu item');
                });
            }
        };
    }
}

const createFolderStepComponentViewFactory = (cfg) => (parent, stepContext, viewContext) => {
    const { step } = stepContext;
    const g = ComponentDom.stepG('folder', stepContext.step.type, stepContext.step.id);
    parent.appendChild(g);
    const x0 = cfg.paddingX * 2 + cfg.iconSize;
    const name = Dom.svg('text', {
        x: x0,
        y: cfg.nameHeight1,
        class: 'sqd-step-folder-name'
    });
    name.textContent = viewContext.getStepName();
    g.appendChild(name);
    const nameBBox = name.getBBox();
    const nameWidth = Math.max(nameBBox.width, cfg.minNameWidth);
    const nameHeight = cfg.nameHeight1 + cfg.nameHeight2;
    const buttonTextValue = createText(step.sequence.length, viewContext.i18n);
    const buttonText = Dom.svg('text', {
        class: 'sqd-step-folder-button-text',
        x: x0 + cfg.buttonPaddingX,
        y: nameHeight + cfg.buttonHeight / 2
    });
    buttonText.textContent = buttonTextValue;
    g.appendChild(buttonText);
    const buttonTextBBox = buttonText.getBBox();
    const buttonIcon = Icons$1.appendPath(g, 'sqd-step-folder-button-icon-path', Icons$1.folderIn, cfg.buttonIconSize);
    Dom.translate(buttonIcon, x0 + cfg.buttonPaddingX * 2 + buttonTextBBox.width, nameHeight + (cfg.buttonHeight - cfg.buttonIconSize) / 2);
    const buttonWidth = cfg.buttonPaddingX * 3 + buttonTextBBox.width + cfg.buttonIconSize;
    const buttonRect = Dom.svg('rect', {
        class: 'sqd-step-folder-button-rect',
        width: buttonWidth,
        height: cfg.buttonHeight,
        rx: 4,
        ry: 4,
        x: x0 + 0.5,
        y: nameHeight + 0.5
    });
    g.appendChild(buttonRect);
    const boxHeight = nameHeight + cfg.buttonHeight + cfg.paddingBottom;
    const boxWidth = x0 + Math.max(nameWidth, buttonWidth) + cfg.paddingX;
    const halfBoxWidth = boxWidth / 2;
    const rect = Dom.svg('rect', {
        x: 0.5,
        y: 0.5,
        class: 'sqd-step-folder-rect',
        width: boxWidth,
        height: boxHeight,
        rx: cfg.rectRadius,
        ry: cfg.rectRadius
    });
    g.insertBefore(rect, name);
    const tabWidth = boxWidth / 4;
    const tabHeight = 6;
    const tab = Dom.svg('rect', {
        class: 'sqd-step-folder-tab-rect',
        x: cfg.paddingX,
        y: -tabHeight,
        width: tabWidth,
        height: tabHeight * 2,
        rx: cfg.rectRadius,
        ry: cfg.rectRadius
    });
    g.insertBefore(tab, rect);
    const iconUrl = viewContext.getStepIconUrl();
    const icon = iconUrl
        ? Dom.svg('image', {
            href: iconUrl
        })
        : Dom.svg('rect', {
            class: 'sqd-step-folder-empty-icon',
            rx: 4,
            ry: 4
        });
    Dom.attrs(icon, {
        x: cfg.paddingX,
        y: (boxHeight - cfg.iconSize) / 2,
        width: cfg.iconSize,
        height: cfg.iconSize
    });
    g.appendChild(icon);
    const inputView = InputView.createRoundInput(g, halfBoxWidth, 0, cfg.inputSize);
    const outputView = OutputView.create(g, halfBoxWidth, boxHeight, cfg.outputSize);
    const placeholderSize = new Vector(boxWidth - cfg.placeholderMargin * 2, boxHeight - cfg.placeholderMargin * 2);
    const placeholder = viewContext.createPlaceholderForArea(g, placeholderSize, PlaceholderDirection.in, stepContext.step.sequence, stepContext.step.sequence.length);
    Dom.translate(placeholder.view.g, cfg.placeholderMargin, cfg.placeholderMargin);
    return {
        g,
        width: boxWidth,
        height: boxHeight,
        joinX: halfBoxWidth,
        placeholders: [placeholder],
        components: null,
        hasOutput: true,
        haveCollapsedChildren: true,
        getClientPosition() {
            return getAbsolutePosition(g);
        },
        resolveClick(click) {
            if (click.element === buttonRect) {
                const openFolderCommand = {
                    type: ClickCommandType.openFolder,
                    step: stepContext.step
                };
                return openFolderCommand;
            }
            if (g.contains(click.element)) {
                return true;
            }
            return null;
        },
        setIsDragging(isDragging) {
            Dom.toggleClass(tab, isDragging, 'sqd-hidden');
            inputView.setIsHidden(isDragging);
            outputView.setIsHidden(isDragging);
        },
        setIsDisabled(isDisabled) {
            Dom.toggleClass(g, isDisabled, 'sqd-disabled');
        },
        setIsSelected(isSelected) {
            Dom.toggleClass(rect, isSelected, 'sqd-selected');
        }
    };
};
function createText(count, i18n) {
    switch (count) {
        case 0:
            return i18n('folderStepComponent.noSteps', 'No steps');
        case 1:
            return i18n('folderStepComponent.1step', '1 step');
        default:
            return i18n('folderStepComponent.nSteps', ':n steps').replace(':n', String(count));
    }
}

const createIconStepComponentViewFactory = (isInterrupted, cfg) => (parent, stepContext, viewContext) => {
    const g = ComponentDom.stepG('icon', stepContext.step.type, stepContext.step.id);
    parent.appendChild(g);
    const r = cfg.size / 2;
    const circle = Dom.svg('circle', {
        cx: r,
        cy: r,
        r: r,
        class: 'sqd-step-icon-circle'
    });
    g.appendChild(circle);
    const iconUrl = viewContext.getStepIconUrl();
    if (iconUrl) {
        const icon = Dom.svg('image', {
            class: 'sqd-step-icon-image',
            href: iconUrl,
            x: (cfg.size - cfg.iconSize) / 2,
            y: (cfg.size - cfg.iconSize) / 2,
            width: cfg.iconSize,
            height: cfg.iconSize
        });
        g.appendChild(icon);
    }
    const isInputViewHidden = stepContext.depth === 0 && stepContext.position === 0 && !stepContext.isInputConnected;
    const isOutputViewHidden = isInterrupted;
    const inputView = isInputViewHidden ? null : InputView.createRoundInput(g, r, 0, cfg.inputSize);
    const outputView = isOutputViewHidden ? null : OutputView.create(g, r, cfg.size, cfg.outputSize);
    return {
        g,
        width: cfg.size,
        height: cfg.size,
        joinX: r,
        placeholders: null,
        components: null,
        hasOutput: !!outputView,
        resolveClick(click) {
            if (this.g.contains(click.element)) {
                return true;
            }
            return null;
        },
        setIsSelected(isSelected) {
            Dom.toggleClass(circle, isSelected, 'sqd-selected');
        },
        setIsDisabled(isDisabled) {
            Dom.toggleClass(this.g, isDisabled, 'sqd-disabled');
        },
        getClientPosition() {
            return getAbsolutePosition(this.g);
        },
        setIsDragging(isDragging) {
            inputView === null || inputView === void 0 ? void 0 : inputView.setIsHidden(isDragging);
            outputView === null || outputView === void 0 ? void 0 : outputView.setIsHidden(isDragging);
        }
    };
};

const defaultViewConfiguration$5 = {
    isRegionEnabled: true,
    paddingY: 10,
    connectionHeight: 20,
    emptyPaddingX: 20,
    emptyPaddingY: 20,
    emptyInputSize: 14,
    emptyOutputSize: 10,
    emptyIconSize: 24
};
class InterruptingLaunchPadStepExtension {
    static create(configuration) {
        return new InterruptingLaunchPadStepExtension(configuration);
    }
    constructor(configuration) {
        var _a;
        this.configuration = configuration;
        this.componentType = 'interruptingLaunchPad';
        this.createComponentView = createLaunchPadStepComponentViewFactory(true, ((_a = this.configuration) === null || _a === void 0 ? void 0 : _a.view) ? Object.assign(Object.assign({}, defaultViewConfiguration$5), this.configuration.view) : defaultViewConfiguration$5);
    }
}

const getDefaultDescription = (step, i18n) => {
    return i18n('largeTaskStepComponent.description', 'Task type: :type').replace(':type', step.type);
};
const createLargeTaskStepComponentViewFactory = (cfg, descriptionProvider) => (parentElement, stepContext, viewContext) => {
    const g = ComponentDom.stepG('large-task', stepContext.step.type, stepContext.step.id);
    parentElement.appendChild(g);
    const textsX = cfg.paddingLeft + cfg.boxWidth + cfg.textMarginLeft;
    const nameText = Dom.svg('text', {
        class: 'sqd-large-task-name-text',
        x: textsX,
        y: cfg.nameY
    });
    nameText.textContent = viewContext.getStepName();
    g.appendChild(nameText);
    const nameBBox = nameText.getBBox();
    const descriptionText = Dom.svg('text', {
        class: 'sqd-large-task-description-text',
        x: textsX,
        y: cfg.descriptionY
    });
    descriptionText.textContent = descriptionProvider
        ? descriptionProvider(stepContext.step)
        : getDefaultDescription(stepContext.step, viewContext.i18n);
    g.appendChild(descriptionText);
    const descriptionBBox = descriptionText.getBBox();
    const box = Dom.svg('rect', {
        class: 'sqd-large-task-box',
        x: cfg.paddingLeft,
        y: cfg.paddingY,
        width: cfg.boxWidth,
        height: cfg.boxHeight,
        rx: cfg.boxRadius,
        ry: cfg.boxRadius
    });
    g.appendChild(box);
    const textsWidth = Math.max(nameBBox.width, descriptionBBox.width, cfg.minTextWidth);
    const width = cfg.boxWidth + textsWidth + cfg.paddingLeft + cfg.paddingRight + cfg.textMarginLeft;
    const height = cfg.boxHeight + cfg.paddingY * 2;
    const iconUrl = viewContext.getStepIconUrl();
    if (iconUrl) {
        const icon = Dom.svg('image', {
            href: iconUrl,
            width: cfg.iconSize,
            height: cfg.iconSize,
            x: cfg.paddingLeft + cfg.boxWidth / 2 - cfg.iconSize / 2,
            y: cfg.paddingY + cfg.boxHeight / 2 - cfg.iconSize / 2
        });
        g.appendChild(icon);
    }
    const isInputViewHidden = stepContext.depth === 0 && stepContext.position === 0 && !stepContext.isInputConnected;
    const inputView = isInputViewHidden ? null : InputView.createRoundInput(g, width / 2, 0, cfg.inputSize);
    const outputView = OutputView.create(g, width / 2, height, cfg.outputSize);
    const rect = Dom.svg('rect', {
        class: 'sqd-large-task-rect',
        x: 0.5,
        y: 0.5,
        width,
        height,
        rx: cfg.radius,
        ry: cfg.radius
    });
    g.insertBefore(rect, nameText);
    return {
        g,
        width,
        height,
        joinX: width / 2,
        placeholders: null,
        components: null,
        hasOutput: true,
        resolveClick(click) {
            if (this.g.contains(click.element)) {
                return true;
            }
            return null;
        },
        setIsSelected(isSelected) {
            Dom.toggleClass(rect, isSelected, 'sqd-selected');
        },
        setIsDisabled(isDisabled) {
            Dom.toggleClass(g, isDisabled, 'sqd-disabled');
        },
        getClientPosition() {
            return getAbsolutePosition(g);
        },
        setIsDragging(isDragging) {
            inputView === null || inputView === void 0 ? void 0 : inputView.setIsHidden(isDragging);
            outputView === null || outputView === void 0 ? void 0 : outputView.setIsHidden(isDragging);
        }
    };
};

class BlankGrid {
    static create() {
        const blank = Dom.svg('rect', {
            class: 'sqd-grid-blank',
            fill: 'none'
        });
        const size = new Vector(200, 200);
        return new BlankGrid(size, blank);
    }
    constructor(size, element) {
        this.size = size;
        this.element = element;
    }
    setScale() {
        // Unused
    }
}

class BlankGridExtension {
    static create() {
        return new BlankGridExtension();
    }
    create() {
        return BlankGrid.create();
    }
}

class BlankGridProExtension {
    static create() {
        const extension = BlankGridExtension.create();
        return new BlankGridProExtension(extension);
    }
    constructor(grid) {
        this.grid = grid;
    }
}

class Icons {
}
Icons.plus = 'M21.712 43.8V25.987H3.9v-4.275h17.812V3.9h4.275v17.813H43.8v4.274H25.987V43.8h-4.275z';
Icons.done = 'M18.137 38.6 4.3 24.763l3.794-3.794 10.043 10.043 21.369-21.37 3.794 3.795L18.137 38.6z';
Icons.loading = 'M24 8c7.83 0 14.51 5.67 15.78 13.4a3.057 3.057 0 0 0 2.98 2.6c.88 0 1.72-.39 2.29-1.06.58-.68.82-1.57.67-2.44C44 9.84 34.8 2 24 2 13.2 2 4 9.84 2.28 20.5c-.15.87.1 1.76.67 2.44.57.67 1.41 1.06 2.29 1.06a3.057 3.057 0 0 0 2.98-2.6C9.49 13.67 16.17 8 24 8zm0 0';
Icons.error = 'M24.045 43.071q-1.549 0-2.647-1.103-1.098-1.102-1.098-2.651 0-1.55 1.103-2.647 1.103-1.099 2.652-1.099 1.549 0 2.647 1.103 1.098 1.103 1.098 2.652 0 1.549-1.103 2.647-1.103 1.098-2.652 1.098zM20.3 30.214V4.5h7.5v25.714h-7.5z';
Icons.collapsed = 'm24 16.6 12 12-2.15 2.15-9.85-9.9-9.85 9.85L12 28.55 24 16.6z';

class ClickablePlaceholderView {
    static create(parent, orientation, width, height, rectRadius, iconSize) {
        const g = Dom.svg('g');
        parent.appendChild(g);
        const squareSize = orientation === PlaceholderGapOrientation.along ? width : height;
        const x = width / 2 - squareSize / 2;
        const y = height / 2 - squareSize / 2;
        const plusG = Dom.svg('g', {
            class: 'sqd-clickable-placeholder'
        });
        Dom.translate(plusG, x, y);
        g.appendChild(plusG);
        const rect = Dom.svg('rect', {
            class: 'sqd-clickable-placeholder-rect',
            width: squareSize,
            height: squareSize,
            rx: rectRadius,
            ry: rectRadius
        });
        plusG.appendChild(rect);
        const icon = Icons$1.appendPath(plusG, 'sqd-clickable-placeholder-icon', Icons.plus, iconSize);
        const iconOffset = (squareSize - iconSize) / 2;
        Dom.translate(icon, iconOffset, iconOffset);
        const rectPlaceholderView = RectPlaceholderView.create(g, squareSize, squareSize, rectRadius, undefined, 0);
        Dom.translate(rectPlaceholderView.g, x, y);
        return new ClickablePlaceholderView(rectPlaceholderView, plusG, g);
    }
    constructor(rectPlaceholderView, plusG, g) {
        this.rectPlaceholderView = rectPlaceholderView;
        this.plusG = plusG;
        this.g = g;
    }
    resolveClick(click) {
        return this.plusG.contains(click.element);
    }
    setIsHover(isHover) {
        this.rectPlaceholderView.setIsHover(isHover);
    }
    setIsVisible(isVisible) {
        this.rectPlaceholderView.setIsVisible(isVisible);
    }
}

class ClickablePlaceholder {
    static create(parent, orientation, size, parentSequence, index, configuration) {
        const view = ClickablePlaceholderView.create(parent, orientation, size.x, size.y, configuration.rectRadius, configuration.iconSize);
        return new ClickablePlaceholder(view, parentSequence, index);
    }
    constructor(view, parentSequence, index) {
        this.view = view;
        this.parentSequence = parentSequence;
        this.index = index;
    }
    getClientRect() {
        return this.view.rectPlaceholderView.rect.getBoundingClientRect();
    }
    setIsHover(isHover) {
        this.view.setIsHover(isHover);
    }
    setIsVisible(isVisible) {
        this.view.setIsVisible(isVisible);
    }
    resolveClick(click) {
        if (this.view.resolveClick(click)) {
            const action = {
                type: 'clickablePlaceholderClicked',
                index: this.index,
                element: click.element
            };
            return {
                type: ClickCommandType.triggerCustomAction,
                action,
                step: null,
                sequence: this.parentSequence
            };
        }
        return null;
    }
}

const defaultConfiguration$8 = {
    gapWidth: 24,
    gapHeight: 88,
    iconSize: 16,
    rectRadius: 6
};
class ClickablePlaceholderExtension {
    static create(configuration) {
        return new ClickablePlaceholderExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$8);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.alongGapSize = new Vector(defaultConfiguration$8.gapWidth, defaultConfiguration$8.gapHeight);
        this.perpendicularGapSize = new Vector(defaultConfiguration$8.gapHeight, defaultConfiguration$8.gapWidth);
    }
    getGapSize(orientation) {
        return orientation === PlaceholderGapOrientation.along ? this.alongGapSize : this.perpendicularGapSize;
    }
    createForGap(parent, parentSequence, index, orientation) {
        const gapSize = this.getGapSize(orientation);
        return ClickablePlaceholder.create(parent, orientation, gapSize, parentSequence, index, this.configuration);
    }
    createForArea(parent, size, direction, parentSequence, index) {
        let iconD;
        if (direction === PlaceholderDirection.in) {
            iconD = Icons$1.folderIn;
        }
        else if (direction === PlaceholderDirection.out) {
            iconD = Icons$1.folderOut;
        }
        return RectPlaceholder.create(parent, size, parentSequence, index, this.configuration.rectRadius, iconD, this.configuration.iconSize);
    }
}

class ClickablePlaceholderProExtension {
    static create(configuration) {
        const placeholder = ClickablePlaceholderExtension.create(configuration);
        return new ClickablePlaceholderProExtension(placeholder);
    }
    constructor(placeholder) {
        this.placeholder = placeholder;
    }
}

class CollapsibleRegionView {
    static create(parent, widths, height, step, isCollapsed, onToggleClicked, cfg) {
        const baseView = DefaultRegionView.create(parent, widths, height);
        const toggleG = Dom.svg('g', {
            class: 'sqd-region-toggle'
        });
        const rect = Dom.svg('rect', {
            class: 'sqd-region-toggle-rect',
            width: cfg.toggleSize,
            height: cfg.toggleSize,
            rx: cfg.toggleRadius,
            ry: cfg.toggleRadius
        });
        toggleG.appendChild(rect);
        const icon = isCollapsed ? Icons$1.expand : Icons.collapsed;
        const iconG = Icons$1.appendPath(toggleG, 'sqd-region-toggle-icon', icon, cfg.toggleIconSize);
        const iconOffset = (cfg.toggleSize - cfg.toggleIconSize) / 2;
        Dom.translate(iconG, iconOffset, iconOffset);
        if (cfg.toggleDecorator) {
            cfg.toggleDecorator.update(toggleG, isCollapsed, widths, height);
        }
        else {
            Dom.attrs(toggleG, {
                x: 0,
                y: 0
            });
        }
        parent.appendChild(toggleG);
        return new CollapsibleRegionView(baseView, step, toggleG, onToggleClicked);
    }
    constructor(baseView, step, toggleG, onToggleClicked) {
        this.baseView = baseView;
        this.step = step;
        this.toggleG = toggleG;
        this.onToggleClicked = onToggleClicked;
    }
    getClientPosition() {
        return this.baseView.getClientPosition();
    }
    resolveClick(click) {
        if (this.toggleG.contains(click.element)) {
            return {
                type: ClickCommandType.rerenderStep,
                step: this.step,
                beforeCallback: this.onToggleClicked
            };
        }
        return this.baseView.resolveClick(click);
    }
    setIsSelected(isSelected) {
        return this.baseView.setIsSelected(isSelected);
    }
}

function createCollapsedStepComponentView(cfg, g, stepContext, viewContext, regionViewFactory) {
    g.classList.add('sqd-collapsed');
    const width = cfg.collapsedWidth;
    const height = cfg.collapsedHeight;
    const joinX = width / 2;
    const halfHeight = height / 2;
    const iconUrl = viewContext.getStepIconUrl();
    InputView.createRectInput(g, joinX, halfHeight, cfg.inputSize, cfg.inputRadius, cfg.inputIconSize, iconUrl);
    JoinView.createStraightJoin(g, new Vector(joinX, stepContext.isInputConnected ? 0 : halfHeight), stepContext.isInputConnected ? height : halfHeight);
    const regionView = regionViewFactory(g, [width], height);
    return {
        g,
        width,
        height,
        joinX,
        placeholders: null,
        components: null,
        hasOutput: true,
        haveCollapsedChildren: true,
        getClientPosition() {
            return regionView.getClientPosition();
        },
        resolveClick(click) {
            const result = regionView.resolveClick(click);
            return result === true || (result === null && g.contains(click.element)) ? true : result;
        },
        setIsDragging() {
            // Nothing...
        },
        setIsSelected(isSelected) {
            regionView.setIsSelected(isSelected);
        },
        setIsDisabled(isDisabled) {
            Dom.toggleClass(g, isDisabled, 'sqd-disabled');
        }
    };
}

const defaultViewConfiguration$4 = {
    collapsedWidth: 128,
    collapsedHeight: 42,
    inputSize: 18,
    inputRadius: 4,
    inputIconSize: 14,
    toggleSize: 20,
    toggleRadius: 0,
    toggleIconSize: 16
};
class CollapsibleRegionComponentViewExtension {
    static create(configuration) {
        if (!configuration) {
            configuration = {
                isCollapsible: () => true
            };
        }
        if (!configuration.view) {
            configuration.view = defaultViewConfiguration$4;
        }
        return new CollapsibleRegionComponentViewExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(parentElement, componentClassName, stepContext, viewContext, contentFactory) {
        const g = ComponentDom.stepG(componentClassName, stepContext.step.type, stepContext.step.id);
        parentElement.appendChild(g);
        const isCollapsible = this.configuration.isCollapsible(stepContext.step.componentType, stepContext.step.type);
        if (!isCollapsible) {
            return contentFactory(g, DefaultRegionView.create);
        }
        const isCollapsed = viewContext.getPreference('isCollapsed') === '1';
        const onToggleClicked = () => viewContext.setPreference('isCollapsed', isCollapsed ? '0' : '1');
        const regionViewFactory = (p, w, h) => CollapsibleRegionView.create(p, w, h, stepContext.step, isCollapsed, onToggleClicked, this.configuration.view);
        if (isCollapsed) {
            return createCollapsedStepComponentView(this.configuration.view, g, stepContext, viewContext, regionViewFactory);
        }
        return contentFactory(g, regionViewFactory);
    }
}

class CollapsibleRegionProExtension {
    static create(configuration) {
        return new CollapsibleRegionProExtension(CollapsibleRegionComponentViewExtension.create(configuration));
    }
    constructor(regionComponentView) {
        this.regionComponentView = regionComponentView;
    }
}

class CounterBadgeView {
    static create(parent, value, isClickable, cfg) {
        const g = Dom.svg('g', {
            class: 'sqd-counter-badge'
        });
        if (isClickable) {
            g.classList.add('sqd-clickable-badge');
        }
        parent.appendChild(g);
        const text = Dom.svg('text', {
            class: 'sqd-counter-badge-text'
        });
        text.textContent = value;
        g.appendChild(text);
        const width = text.getBBox().width + cfg.paddingX * 2;
        const rect = Dom.svg('rect', {
            class: 'sqd-counter-badge-rect',
            width,
            height: cfg.height,
            rx: cfg.radius,
            ry: cfg.radius
        });
        g.insertBefore(rect, text);
        Dom.translate(text, width / 2, cfg.height / 2);
        return new CounterBadgeView(parent, g, width, cfg.height);
    }
    constructor(parent, g, width, height) {
        this.parent = parent;
        this.g = g;
        this.width = width;
        this.height = height;
    }
    resolveClick(click) {
        return this.g.contains(click.element);
    }
    destroy() {
        this.parent.removeChild(this.g);
    }
}

class CounterBadge {
    static create(parentElement, stepContext, valueProvider, isClickable, viewConfiguration) {
        return new CounterBadge(parentElement, stepContext, valueProvider, isClickable, viewConfiguration);
    }
    constructor(parentElement, stepContext, valueProvider, isClickable, viewConfiguration) {
        this.parentElement = parentElement;
        this.stepContext = stepContext;
        this.valueProvider = valueProvider;
        this.isClickable = isClickable;
        this.viewConfiguration = viewConfiguration;
        this.view = null;
    }
    update() {
        const value = this.valueProvider(this.stepContext.step);
        if (value) {
            if (this.view && this.lastValue !== value) {
                this.view.destroy();
                this.view = null;
            }
            if (!this.view) {
                this.view = CounterBadgeView.create(this.parentElement, value, this.isClickable, this.viewConfiguration);
            }
        }
        else if (this.view) {
            this.view.destroy();
            this.view = null;
        }
        this.lastValue = value;
        return null;
    }
    resolveClick(click) {
        if (this.view && this.isClickable && this.view.resolveClick(click)) {
            const action = {
                type: 'counterBadgeClicked'
            };
            const command = {
                type: ClickCommandType.triggerCustomAction,
                action,
                sequence: this.stepContext.parentSequence,
                step: this.stepContext.step
            };
            return command;
        }
        return null;
    }
}

const defaultViewConfiguration$3 = {
    paddingX: 5,
    height: 22,
    radius: 10
};
class CounterBadgeExtension {
    static create(configuration) {
        return new CounterBadgeExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.id = 'counter';
        this.createStartValue = () => null;
    }
    createForStep(parentElement, _, stepContext) {
        return CounterBadge.create(parentElement, stepContext, this.configuration.getValue, !!this.configuration.isClickable, this.configuration.view || defaultViewConfiguration$3);
    }
}

class CounterBadgeProExtension {
    static create(configuration) {
        const badge = CounterBadgeExtension.create(configuration);
        return new CounterBadgeProExtension([badge]);
    }
    constructor(badges) {
        this.badges = badges;
    }
}

class CrossGrid {
    static create(configuration) {
        const path = Dom.svg('path', {
            class: 'sqd-cross-grid-path',
            fill: 'none'
        });
        const size = new Vector(configuration.gridSizeX, configuration.gridSizeY);
        return new CrossGrid(size, path, configuration.crossX, configuration.crossY);
    }
    constructor(size, element, crossX, crossY) {
        this.size = size;
        this.element = element;
        this.crossX = crossX;
        this.crossY = crossY;
    }
    setScale(scale) {
        const x = this.crossX * scale;
        const y = this.crossY * scale;
        const hx = x / 2;
        const hy = y / 2;
        Dom.attrs(this.element, {
            d: `M 0 ${hy} L ${hx} ${hy} L ${hx} 0 L ${hx} ${hy} L ${x} ${hy} L ${hx} ${hy} L ${hx} ${y} L ${hx} ${hy} L 0 ${hy}`
        });
    }
}

const defaultConfiguration$7 = {
    gridSizeX: 48,
    gridSizeY: 48,
    crossX: 10,
    crossY: 10
};
class CrossGridExtension {
    static create(configuration) {
        return new CrossGridExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$7);
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    create() {
        return CrossGrid.create(this.configuration);
    }
}

class CrossGridProExtension {
    static create(configuration) {
        const extension = CrossGridExtension.create(configuration);
        return new CrossGridProExtension(extension);
    }
    constructor(grid) {
        this.grid = grid;
    }
}

class CustomContextMenuProExtension {
    static create(configuration) {
        const contextMenu = CustomContextMenuExtension.create(configuration);
        return new CustomContextMenuProExtension(contextMenu);
    }
    constructor(contextMenu) {
        this.contextMenu = contextMenu;
    }
}

class CustomDraggedComponent {
    static create(parent, step, isAttached, configuration) {
        const result = configuration.create(step, isAttached);
        parent.appendChild(result.element);
        return new CustomDraggedComponent(result.width, result.height, result.destroy);
    }
    constructor(width, height, destroyHandler) {
        this.width = width;
        this.height = height;
        this.destroyHandler = destroyHandler;
        this.scale = 1;
    }
    destroy() {
        if (this.destroyHandler) {
            this.destroyHandler();
        }
    }
}

class CustomDraggedComponentExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(parent, step, isAttached) {
        return CustomDraggedComponent.create(parent, step, isAttached, this.configuration);
    }
}

class CustomDraggedComponentProExtension {
    static create(configuration) {
        const dragComponent = new CustomDraggedComponentExtension(configuration);
        return new CustomDraggedComponentProExtension(dragComponent);
    }
    constructor(draggedComponent) {
        this.draggedComponent = draggedComponent;
    }
}

class CustomStepsProExtension {
    static create(configuration) {
        return new CustomStepsProExtension(configuration.steps);
    }
    constructor(steps) {
        this.steps = steps;
    }
}

class CustomViewportController {
    static create(api, configuration) {
        const defaultController = DefaultViewportController.create(api, configuration === null || configuration === void 0 ? void 0 : configuration.defaultController);
        return new CustomViewportController(api, defaultController, configuration);
    }
    constructor(api, defaultController, configuration) {
        this.api = api;
        this.defaultController = defaultController;
        this.configuration = configuration;
        this.context = {
            getCanvasPosition: () => this.api.getCanvasPosition(),
            getCanvasSize: () => this.api.getCanvasSize(),
            getRootComponentSize: () => this.api.getRootComponentSize(),
            getViewport: () => this.api.getViewport()
        };
        this.smoothDeltaYLimit = this.defaultController.smoothDeltaYLimit;
    }
    getDefault() {
        return this.configuration.getDefault ? this.configuration.getDefault(this.context) : this.defaultController.getDefault();
    }
    getZoomed(direction) {
        return this.configuration.getZoomed
            ? this.configuration.getZoomed(this.context, direction)
            : this.defaultController.getZoomed(direction);
    }
    getFocusedOnComponent(componentPosition, componentSize, componentType, componentElement) {
        return this.configuration.getFocusedOnComponent
            ? this.configuration.getFocusedOnComponent(this.context, componentPosition, componentSize, componentType, componentElement)
            : this.defaultController.getFocusedOnComponent(componentPosition, componentSize);
    }
    getNextScale(scale, direction) {
        return this.defaultController.getNextScale(scale, direction);
    }
    limitScale(scale) {
        return this.defaultController.limitScale(scale);
    }
}

class CustomViewportControllerExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(api) {
        return CustomViewportController.create(api, this.configuration);
    }
}

class CustomViewportControllerProExtension {
    static create(configuration) {
        const controller = new CustomViewportControllerExtension(configuration);
        return new CustomViewportControllerProExtension(controller);
    }
    constructor(viewportController) {
        this.viewportController = viewportController;
    }
}
/**
 * @deprecated Use `CustomViewportControllerProExtension` instead.
 */
class CustomViewPortControllerProExtension extends CustomViewportControllerProExtension {
}

class DotGrid {
    static create(configuration) {
        const circle = Dom.svg('circle', {
            class: 'sqd-grid-dot'
        });
        const size = new Vector(configuration.gridSizeX, configuration.gridSizeY);
        return new DotGrid(size, circle, configuration.dotR);
    }
    constructor(size, element, r) {
        this.size = size;
        this.element = element;
        this.r = r;
    }
    setScale(scale) {
        const r = this.r * scale;
        Dom.attrs(this.element, {
            r,
            cx: r / 2 + 0.5,
            cy: r / 2 + 0.5
        });
    }
}

const defaultConfiguration$6 = {
    gridSizeX: 16,
    gridSizeY: 16,
    dotR: 1
};
class DotGridExtension {
    static create(configuration) {
        return new DotGridExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$6);
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    create() {
        return DotGrid.create(this.configuration);
    }
}

class DotGridProExtension {
    static create(configuration) {
        const extension = DotGridExtension.create(configuration);
        return new DotGridProExtension(extension);
    }
    constructor(grid) {
        this.grid = grid;
    }
}

class DoubleClickBehavior {
    constructor(parent, command, customActionController, doubleClickTime) {
        this.parent = parent;
        this.command = command;
        this.customActionController = customActionController;
        this.doubleClickTime = doubleClickTime;
    }
    onStart(position) {
        return this.parent.onStart(position);
    }
    onMove(delta) {
        return this.parent.onMove(delta);
    }
    onEnd(interrupt, element, previousEndToken) {
        if (!interrupt && SelectStepBehaviorEndToken.is(previousEndToken) && previousEndToken.stepId === this.command.component.step.id) {
            const now = Date.now();
            if (previousEndToken.time + this.doubleClickTime > now) {
                const action = {
                    type: 'doubleClicked'
                };
                this.customActionController.trigger(action, this.command.component.step, this.command.component.parentSequence);
                return;
            }
        }
        return this.parent.onEnd(interrupt, element, previousEndToken);
    }
}

class DoubleClickBehaviorWrapper {
    constructor(customActionController, configuration) {
        this.customActionController = customActionController;
        this.configuration = configuration;
    }
    wrap(behavior, commandOrNull) {
        if (commandOrNull && commandOrNull.type === ClickCommandType.selectStep) {
            return new DoubleClickBehavior(behavior, commandOrNull, this.customActionController, this.configuration.doubleClickTime);
        }
        return behavior;
    }
}

class DoubleClickBehaviorWrapperExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(customActionController) {
        return new DoubleClickBehaviorWrapper(customActionController, this.configuration);
    }
}

const defaultConfiguration$5 = {
    doubleClickTime: 800
};
class DoubleClickProExtension {
    static create(configuration) {
        return new DoubleClickProExtension(configuration || defaultConfiguration$5);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.clickBehaviorWrapperExtension = new DoubleClickBehaviorWrapperExtension(this.configuration);
    }
}

class ExternalControlBarProExtension {
    static create(configuration) {
        return new ExternalControlBarProExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.daemons = [new ExternalControlBarDaemonExtension(this.configuration)];
    }
}

class ExternalEditorDaemonExtension {
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(api) {
        return ExternalEditorDaemon.create(api, this.configuration);
    }
}

class ExternalEditorProExtension {
    static create(configuration) {
        return new ExternalEditorProExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.daemons = [new ExternalEditorDaemonExtension(this.configuration)];
    }
}

class ExternalPathBarProExtension {
    static create(configuration) {
        return new ExternalPathBarProExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.daemons = [new ExternalPathBarDaemonExtension(this.configuration)];
    }
}

class ExternalToolboxProExtension {
    static create(configuration) {
        return new ExternalToolboxProExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.daemons = [new ExternalToolboxDaemonExtension(this.configuration)];
    }
}

class IconBadgeView {
    static create(parent, data, cfg) {
        const halfSize = cfg.size / 2;
        const g = Dom.svg('g', {
            class: 'sqd-icon-badge'
        });
        g.classList.add(`sqd-icon-badge-type-${data.type}`);
        if (data.isClickable) {
            g.classList.add('sqd-clickable-badge');
        }
        parent.appendChild(g);
        const circle = Dom.svg('circle', {
            cx: halfSize,
            cy: halfSize,
            r: halfSize,
            class: 'sqd-icon-badge-circle'
        });
        g.appendChild(circle);
        let box = g;
        if (data.feature === IconBadgeFeature.rotate) {
            const rotate = Dom.svg('g', {
                class: 'sqd-icon-badge-rotate'
            });
            g.appendChild(rotate);
            box = rotate;
        }
        const icon = Icons$1.appendPath(box, 'sqd-icon-badge-icon', data.d, cfg.iconSize);
        const offset = halfSize - cfg.iconSize / 2;
        Dom.translate(icon, offset, offset);
        return new IconBadgeView(parent, g, cfg.size, cfg.size);
    }
    constructor(parent, g, width, height) {
        this.parent = parent;
        this.g = g;
        this.width = width;
        this.height = height;
    }
    resolveClick(click) {
        return this.g.contains(click.element);
    }
    destroy() {
        this.parent.removeChild(this.g);
    }
}

class IconBadge {
    static create(parentElement, stepContext, statusProvider, viewConfiguration) {
        return new IconBadge(parentElement, stepContext, statusProvider, viewConfiguration);
    }
    constructor(parentElement, stepContext, statusProvider, viewConfiguration) {
        this.parentElement = parentElement;
        this.stepContext = stepContext;
        this.statusProvider = statusProvider;
        this.viewConfiguration = viewConfiguration;
        this.view = null;
    }
    update() {
        const data = this.statusProvider(this.stepContext.step);
        if (data) {
            if (this.view && this.lastType !== data.type) {
                this.view.destroy();
                this.view = null;
            }
            if (!this.view) {
                this.view = IconBadgeView.create(this.parentElement, data, this.viewConfiguration);
            }
            this.lastType = data.type;
            this.lastIsClickable = data.isClickable;
        }
        else if (this.view) {
            this.view.destroy();
            this.view = null;
            this.lastType = null;
        }
        return null;
    }
    resolveClick(click) {
        if (this.view && this.lastType && this.lastIsClickable && this.view.resolveClick(click)) {
            const action = {
                type: 'iconBadgeClicked',
                badgeType: this.lastType
            };
            const command = {
                type: ClickCommandType.triggerCustomAction,
                action,
                sequence: this.stepContext.parentSequence,
                step: this.stepContext.step
            };
            return command;
        }
        return null;
    }
}

const defaultViewConfiguration$2 = {
    size: 22,
    iconSize: 16
};
class IconBadgeExtension {
    static create(configuration) {
        return new IconBadgeExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.id = 'icon';
        this.createStartValue = () => null;
    }
    createForStep(parentElement, _, stepContext) {
        var _a;
        return IconBadge.create(parentElement, stepContext, this.configuration.iconProvider, (_a = this.configuration.view) !== null && _a !== void 0 ? _a : defaultViewConfiguration$2);
    }
}

class IconBadgeProExtension {
    static create(configuration) {
        const badge = IconBadgeExtension.create(configuration);
        return new IconBadgeProExtension([badge]);
    }
    constructor(badges) {
        this.badges = badges;
    }
}

const defaultViewConfiguration$1 = {
    size: 22,
    iconSize: 16
};
class LoadingBadgeExtension {
    static create(configuration) {
        return new LoadingBadgeExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.id = 'loading';
        this.createStartValue = () => null;
    }
    createForStep(parentElement, _, stepContext) {
        const iconProvider = step => {
            const status = this.configuration.statusProvider(step);
            return status ? getIconData(status) : null;
        };
        return IconBadge.create(parentElement, stepContext, iconProvider, this.configuration.view || defaultViewConfiguration$1);
    }
}
function getIconData(status) {
    switch (status) {
        case LoadingBadgeStatus.loading:
            return {
                type: 'loading',
                d: Icons.loading,
                feature: IconBadgeFeature.rotate
            };
        case LoadingBadgeStatus.error:
            return {
                type: 'error',
                d: Icons.error
            };
        case LoadingBadgeStatus.loaded:
            return {
                type: 'loaded',
                d: Icons.done
            };
        default:
            throw new Error('Unsupported loading status');
    }
}

class LoadingBadgeProExtension {
    static create(configuration) {
        const badge = LoadingBadgeExtension.create(configuration);
        return new LoadingBadgeProExtension([badge]);
    }
    constructor(badges) {
        this.badges = badges;
    }
}

class MinimalRootComponentView {
    static create(parent, sequence, context, cfg) {
        const g = Dom.svg('g', {
            class: 'sqd-root-minimal'
        });
        parent.appendChild(g);
        const sequenceContext = {
            sequence,
            depth: 0,
            isInputConnected: false,
            isOutputConnected: false,
            isPreview: false
        };
        const sequenceComponent = context.services.sequenceComponent.create(g, sequenceContext, context);
        const view = sequenceComponent.view;
        Dom.translate(view.g, 0, cfg.badgesHeight);
        if (sequence.length === 0) {
            const frame = Dom.svg('rect', {
                class: 'sqd-root-minimal-empty',
                rx: 4,
                ry: 4,
                width: view.width,
                height: view.height
            });
            Dom.translate(frame, 0, cfg.badgesHeight);
            g.insertBefore(frame, view.g);
        }
        // TODO: Badges component should have configurable how to center badges.
        const badges = Badges.createForRoot(g, new Vector(view.joinX, cfg.badgesHeight / 2), context);
        const totalHeight = view.height + cfg.badgesHeight * 2;
        return new MinimalRootComponentView(g, view.width, totalHeight, view.joinX, sequenceComponent, badges);
    }
    constructor(g, width, height, joinX, component, badges) {
        this.g = g;
        this.width = width;
        this.height = height;
        this.joinX = joinX;
        this.component = component;
        this.badges = badges;
    }
}

class MinimalRootComponent {
    static create(parentElement, sequence, context, cfg) {
        const view = MinimalRootComponentView.create(parentElement, sequence, context, cfg);
        return new MinimalRootComponent(view);
    }
    constructor(view) {
        this.view = view;
    }
    resolveClick(click) {
        return this.view.component.resolveClick(click);
    }
    findById(stepId) {
        return this.view.component.findById(stepId);
    }
    resolvePlaceholders(skipComponent, result) {
        this.view.component.resolvePlaceholders(skipComponent, result);
    }
    updateBadges(result) {
        this.view.badges.update(result);
        this.view.component.updateBadges(result);
    }
}

const defaultConfiguration$4 = {
    view: {
        badgesHeight: 32
    }
};
class MinimalRootComponentExtension {
    static create(configuration) {
        return new MinimalRootComponentExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$4);
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    create(parentElement, sequence, _, context) {
        return MinimalRootComponent.create(parentElement, sequence, context, this.configuration.view);
    }
}

class MinimalRootComponentProExtension {
    static create(configuration) {
        return new MinimalRootComponentProExtension(configuration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.rootComponent = MinimalRootComponentExtension.create(this.configuration);
    }
}

class ModernWheelController {
    static create(viewportApi, workspaceApi) {
        return new ModernWheelController(viewportApi, workspaceApi);
    }
    constructor(viewportApi, workspaceApi) {
        this.viewportApi = viewportApi;
        this.workspaceApi = workspaceApi;
    }
    onWheel(e) {
        if (e.ctrlKey) {
            this.viewportApi.handleWheelEvent(e);
            return;
        }
        const viewport = this.workspaceApi.getViewport();
        this.workspaceApi.setViewport({
            position: viewport.position.subtract(new Vector(-e.deltaX, -e.deltaY)),
            scale: viewport.scale
        });
    }
}

class ModernWheelControllerExtension {
    constructor() {
        this.create = ModernWheelController.create;
    }
}

class ModernWheelProExtension {
    constructor() {
        this.wheelController = new ModernWheelControllerExtension();
    }
    static create() {
        return new ModernWheelProExtension();
    }
}

class CustomStepComponentViewWrapperProExtension {
    static create(stepComponentViewWrapper) {
        return new CustomStepComponentViewWrapperProExtension(stepComponentViewWrapper);
    }
    constructor(stepComponentViewWrapper) {
        this.stepComponentViewWrapper = stepComponentViewWrapper;
    }
}

const ITEMS_LIMIT = 3;
class PathBarView {
    static create(parent, i18n) {
        const root = Dom.element('div', {
            class: 'sqd-path-bar'
        });
        const resetButton = Dom.element('div', {
            class: 'sqd-path-bar-reset-button',
            title: i18n('pathBar.goToTop', 'Go to top')
        });
        const resetButtonIcon = Icons$1.createSvg('sqd-path-bar-reset-button-icon', Icons$1.folderUp);
        const items = Dom.element('div', {
            class: 'sqd-path-bar-items'
        });
        root.appendChild(resetButton);
        root.appendChild(items);
        resetButton.appendChild(resetButtonIcon);
        parent.appendChild(root);
        return new PathBarView(root, items, resetButton);
    }
    constructor(root, items, resetButton) {
        this.root = root;
        this.items = items;
        this.resetButton = resetButton;
    }
    bindOnItemClick(handler) {
        this.onClickHandler = handler;
    }
    bindOnResetClick(handler) {
        this.resetButton.addEventListener('click', e => {
            e.preventDefault();
            handler();
        });
    }
    reload(names) {
        const isHidden = names === null;
        Dom.toggleClass(this.root, isHidden, 'sqd-hidden');
        if (isHidden) {
            return;
        }
        this.items.innerHTML = '';
        const startIndex = Math.max(0, names.length - ITEMS_LIMIT);
        for (let index = startIndex; index < names.length; index++) {
            const name = names[index];
            if (index > 0) {
                const separator = Dom.element('span', {
                    class: 'sqd-path-bar-separator'
                });
                separator.innerText = index === startIndex ? '...' : '/';
                this.items.appendChild(separator);
            }
            const item = Dom.element('span', {
                class: 'sqd-path-bar-item'
            });
            item.innerText = name;
            item.addEventListener('click', e => {
                e.preventDefault();
                if (this.onClickHandler) {
                    this.onClickHandler(index);
                }
            });
            this.items.appendChild(item);
        }
    }
}

class PathBar {
    static create(parent, api) {
        const view = PathBarView.create(parent, api.i18n);
        const bar = new PathBar(view, api.pathBar);
        api.pathBar.onStateChanged.subscribe(bar.reloadPath);
        view.bindOnItemClick(index => bar.onItemClicked(index));
        view.bindOnResetClick(() => bar.onResetClicked());
        bar.reloadPath();
        return bar;
    }
    constructor(view, api) {
        this.view = view;
        this.api = api;
        this.reloadPath = () => {
            const names = this.api.getFolderPathStepNames();
            this.view.reload(names.length === 0 ? null : names);
        };
    }
    updateLayout() {
        //
    }
    destroy() {
        //
    }
    onItemClicked(index) {
        const currentPath = this.api.getFolderPath();
        const newPath = currentPath.splice(0, index + 1);
        this.api.setFolderPath(newPath);
    }
    onResetClicked() {
        this.api.setFolderPath([]);
    }
}

class PathBarExtension {
    constructor() {
        this.create = PathBar.create;
    }
}

const defaultConfiguration$3 = {
    view: {
        rectRadius: 5,
        nameHeight1: 20,
        nameHeight2: 16,
        paddingX: 12,
        paddingBottom: 12,
        iconSize: 22,
        buttonHeight: 24,
        buttonPaddingX: 6,
        buttonIconSize: 14,
        minNameWidth: 40,
        placeholderMargin: 10,
        inputSize: 14,
        outputSize: 10
    }
};
class FolderStepExtension {
    static create(configuration) {
        return new FolderStepExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$3);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.componentType = 'folder';
        this.createComponentView = createFolderStepComponentViewFactory(this.configuration.view);
    }
}

const defaultConfiguration$2 = {
    view: {
        size: 52,
        iconSize: 28,
        inputSize: 14,
        outputSize: 10
    }
};
class IconStepExtension {
    static create(configuration) {
        return new IconStepExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$2);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.componentType = 'icon';
        this.createComponentView = createIconStepComponentViewFactory(false, this.configuration.view);
    }
}

const defaultConfiguration$1 = {
    view: {
        size: 52,
        iconSize: 28,
        inputSize: 14,
        outputSize: 10
    }
};
class InterruptingIconStepExtension {
    static create(configuration) {
        return new InterruptingIconStepExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration$1);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.componentType = 'interruptingIcon';
        this.createComponentView = createIconStepComponentViewFactory(true, this.configuration.view);
    }
}

const defaultConfiguration = {
    view: {
        paddingLeft: 12,
        paddingRight: 12,
        paddingY: 10,
        textMarginLeft: 12,
        minTextWidth: 70,
        iconSize: 22,
        radius: 5,
        inputSize: 14,
        outputSize: 10
    }
};
class InterruptingTaskStepExtension {
    static create(configuration) {
        return new InterruptingTaskStepExtension(configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration);
    }
    constructor(configuration) {
        this.configuration = configuration;
        this.componentType = 'interruptingTask';
        this.createComponentView = createTaskStepComponentViewFactory(true, this.configuration.view);
    }
}

const defaultViewConfiguration = {
    textMarginLeft: 12,
    minTextWidth: 140,
    nameY: 22,
    descriptionY: 44,
    paddingLeft: 12,
    paddingRight: 12,
    paddingY: 12,
    boxWidth: 40,
    boxHeight: 40,
    boxRadius: 20,
    iconSize: 22,
    inputSize: 14,
    outputSize: 10,
    radius: 4
};
class LargeTaskStepExtension {
    static create(configuration) {
        return new LargeTaskStepExtension(configuration);
    }
    constructor(configuration) {
        var _a, _b, _c;
        this.configuration = configuration;
        this.componentType = 'largeTask';
        this.createComponentView = createLargeTaskStepComponentViewFactory((_b = (_a = this.configuration) === null || _a === void 0 ? void 0 : _a.view) !== null && _b !== void 0 ? _b : defaultViewConfiguration, (_c = this.configuration) === null || _c === void 0 ? void 0 : _c.descriptionProvider);
    }
}

class StepsProExtension {
    static create(configuration) {
        const uiComponents = (configuration === null || configuration === void 0 ? void 0 : configuration.pathBar) === false ? undefined : [new PathBarExtension()];
        return new StepsProExtension([
            IconStepExtension.create(configuration === null || configuration === void 0 ? void 0 : configuration.icon),
            InterruptingIconStepExtension.create(configuration === null || configuration === void 0 ? void 0 : configuration.interruptingIcon),
            InterruptingLaunchPadStepExtension.create(configuration === null || configuration === void 0 ? void 0 : configuration.interruptingLaunchPad),
            InterruptingTaskStepExtension.create(configuration === null || configuration === void 0 ? void 0 : configuration.interruptingTask),
            FolderStepExtension.create(configuration === null || configuration === void 0 ? void 0 : configuration.folder),
            LargeTaskStepExtension.create(configuration === null || configuration === void 0 ? void 0 : configuration.largeTask)
        ], uiComponents);
    }
    constructor(steps, uiComponents) {
        this.steps = steps;
        this.uiComponents = uiComponents;
    }
}

export { BlankGridProExtension, ClickablePlaceholderProExtension, CollapsibleRegionProExtension, CounterBadgeProExtension, CrossGridProExtension, CustomContextMenuExtension, CustomContextMenuProExtension, CustomDraggedComponentProExtension, CustomSelectedStepIdExternalEditorController, CustomStepComponentViewWrapperProExtension, CustomStepsProExtension, CustomViewPortControllerProExtension, CustomViewportControllerProExtension, DotGridProExtension, DoubleClickProExtension, ExternalControlBarController, ExternalControlBarDaemonExtension, ExternalControlBarProExtension, ExternalEditorDaemon, ExternalEditorProExtension, ExternalPathBarController, ExternalPathBarDaemon, ExternalPathBarDaemonExtension, ExternalPathBarProExtension, ExternalToolboxController, ExternalToolboxDaemonExtension, ExternalToolboxProExtension, IconBadgeFeature, IconBadgeProExtension, InterruptingLaunchPadStepExtension, LoadingBadgeProExtension, LoadingBadgeStatus, MinimalRootComponentProExtension, ModernWheelProExtension, StepsProExtension, createFolderStepComponentViewFactory, createIconStepComponentViewFactory, createLargeTaskStepComponentViewFactory };
